import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const testimonials = [
  {
    name: 'Marcus T.',
    result: 'Lost 28 lbs in 12 weeks',
    // Escape the apostrophe or use double quotes for the whole string
    quote:
      "Benny's accountability kept me on track when I wanted to quit. Best investment I've made in myself.",
    rating: 5,
  },
  {
    name: 'Sarah K.',
    result: 'Gained 15 lbs of muscle',
    quote:
      'The custom workout plan was exactly what I needed. I finally feel strong and confident.',
    rating: 5,
  },
  {
    name: 'David R.',
    result: 'Down 3 pant sizes',
    quote:
      "I tried everything before this. Benny's approach is different — it actually works long-term.",
    rating: 5,
  },
];

const faqs = [
  {
    q: 'How do I know which plan is right for me?',
    a: 'Book a free 30 minute consultation and we will assess your goals, schedule, and lifestyle to recommend the best fit. Foundation ($297/mo) is ideal for beginners building structure. Ascend ($597/mo) is for professionals who want faster results and direct coaching. Legacy Elite ($1,500/mo) is for executives and high performers who demand the absolute best.',
  },
  {
    q: 'Is there a contract or long term commitment?',
    a: 'No contracts. All plans are month to month. You can cancel anytime with 48 hours notice before your next billing date. We believe in earning your business every single month.',
  },
  {
    q: 'Are sessions done in person or online?',
    a: 'Both options are available. Online coaching is available worldwide via video call and app based check ins. In person sessions are available depending on location. Ask during your consultation. Legacy Elite clients receive priority scheduling for all session types.',
  },
  {
    q: 'How fast will I see results?',
    a: 'Most clients notice changes in energy and strength within 2 weeks. Visible body composition changes typically show up around week 4 to 6. Long term transformation happens in 12+ weeks with consistency. Legacy Elite clients typically see accelerated results due to weekly 1 on 1 video calls and executive performance coaching.',
  },
  {
    q: 'What is the difference between Ascend and Legacy Elite?',
    a: 'Ascend ($597/mo) includes weekly check ins, form review, video feedback, direct messaging, and mindset training modules. Legacy Elite ($1,500/mo) adds weekly 1 on 1 video calls, executive performance coaching, bloodwork and supplement guidance, stress and recovery optimization, VIP private access, and a quarterly performance reset plan — everything for the man who demands the highest level.',
  },
  {
    q: 'What happens during the first week?',
    a: 'Week 1 is all about assessment and setup. You will complete a fitness evaluation, set clear goals, receive your custom workout plan, and have your first coaching session. You will know exactly what to do from day one regardless of which package you choose.',
  },
  {
    q: 'What if I miss a session?',
    a: 'Life happens. Sessions can be rescheduled with at least 24 hours notice. Missed sessions without notice may not be rescheduled within the same billing period. We hold spots for you, so we ask for mutual respect.',
  },
  {
    q: 'Do you offer refunds?',
    a: 'Yes. All packages come with a 90 Day Results Guarantee. If you are not satisfied after following the program, we will refund every penny — no questions asked. We stand fully behind our results.',
  },
  {
    q: 'Can I upgrade or downgrade my plan later?',
    a: 'Absolutely. You can move from Foundation to Ascend, or from Ascend to Legacy Elite at any time. Upgrades take effect immediately. Downgrades apply at the start of your next billing cycle. We want you in the plan that fits your life and goals.',
  },
  {
    q: 'How do payments work?',
    a: 'All plans are billed monthly via credit card or ACH. Foundation is $297/mo, Ascend is $597/mo, and Legacy Elite is $1,500/mo. You will receive an invoice 3 days before each billing cycle. No hidden fees, ever.',
  },
];

export default function ProgramsFAQ() {
  // Start with no FAQ opened; using null makes the intent clearer
  const [open, setOpen] = useState<number | null>(null);
  
  const testimonialsHeaderRef = useScrollAnimation({ delay: 0 });
  const testimonial1Ref = useScrollAnimation({ delay: 0 });
  const testimonial2Ref = useScrollAnimation({ delay: 150 });
  const testimonial3Ref = useScrollAnimation({ delay: 300 });
  
  const faqHeaderRef = useScrollAnimation({ delay: 0 });
  const faq0Ref = useScrollAnimation({ delay: 0 });
  const faq1Ref = useScrollAnimation({ delay: 50 });
  const faq2Ref = useScrollAnimation({ delay: 100 });
  const faq3Ref = useScrollAnimation({ delay: 150 });
  const faq4Ref = useScrollAnimation({ delay: 200 });
  const faq5Ref = useScrollAnimation({ delay: 250 });
  const faq6Ref = useScrollAnimation({ delay: 300 });
  const faq7Ref = useScrollAnimation({ delay: 350 });
  const faq8Ref = useScrollAnimation({ delay: 400 });
  const faq9Ref = useScrollAnimation({ delay: 450 });

  const faqRefs = [faq0Ref, faq1Ref, faq2Ref, faq3Ref, faq4Ref, faq5Ref, faq6Ref, faq7Ref, faq8Ref, faq9Ref];
  const testimonialRefs = [testimonial1Ref, testimonial2Ref, testimonial3Ref];

  return (
    <>
      {/* Testimonials Strip */}
      <section className="py-16 px-4" style={{ background: '#ffffff' }}>
        <div className="max-w-7xl mx-auto">
          <div ref={testimonialsHeaderRef} className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
              Real Results
            </p>
            <h2 className="text-3xl font-extrabold" style={{ color: '#1a1a1a' }}>
              What Clients Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                ref={testimonialRefs[i]}
                className="rounded-xl p-6 transition-all duration-300 hover:shadow-xl"
                style={{
                  background: '#ffffff',
                  border: '1.5px solid #e8e0d0',
                }}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, idx) => (
                    <div key={idx} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm" style={{ color: '#B8860B' }}></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#333' }}>
                  &quot;{t.quote}&quot;
                </p>
                <div className="pt-3" style={{ borderTop: '1px solid #f0ece4' }}>
                  <p className="text-xs font-bold" style={{ color: '#1a1a1a' }}>
                    {t.name}
                  </p>
                  <p className="text-xs font-semibold mt-0.5" style={{ color: '#8B0000' }}>
                    {t.result}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4" style={{ background: '#f9f6f0' }}>
        <div className="max-w-3xl mx-auto">
          <div ref={faqHeaderRef} className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#B8860B' }}>
              Got Questions?
            </p>
            <h2 className="text-3xl font-extrabold" style={{ color: '#1a1a1a' }}>
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                ref={faqRefs[i]}
                className="rounded-xl overflow-hidden transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: open === i ? '1.5px solid #B8860B' : '1.5px solid #e8e0d0',
                  boxShadow: open === i ? '0 4px 20px rgba(184,134,11,0.1)' : 'none',
                }}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  <span className="text-sm font-bold pr-4" style={{ color: '#1a1a1a' }}>
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-${open === i ? 'subtract' : 'add'}-line text-lg transition-transform duration-300`}
                      style={{ color: '#B8860B' }}
                    ></i>
                  </div>
                </button>
                {open === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm leading-relaxed" style={{ color: '#666' }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}